/**
 * Public "Follow Us" links.
 *
 * Create React App inlines REACT_APP_* at build time. Changing a URL on
 * Vercel takes effect on the next deploy of that environment. No Git commit
 * is required. Leave a variable unset until the real LookBel account exists.
 *
 *   REACT_APP_INSTAGRAM_URL
 *   REACT_APP_TIKTOK_URL
 *   REACT_APP_FACEBOOK_URL
 *   REACT_APP_PINTEREST_URL
 *
 * A value is used only when it is https on that network's own host and has a
 * profile path. The bare homepage, http, javascript:, "#", and other domains
 * are ignored, and that icon stays hidden.
 */

export type SocialNetwork = 'instagram' | 'tiktok' | 'facebook' | 'pinterest';

const HOSTS: Record<SocialNetwork, readonly string[]> = {
  instagram: ['instagram.com', 'www.instagram.com'],
  tiktok: ['tiktok.com', 'www.tiktok.com'],
  facebook: ['facebook.com', 'www.facebook.com'],
  pinterest: ['pinterest.com', 'www.pinterest.com'],
};

const ENV_NAMES: Record<SocialNetwork, string> = {
  instagram: 'REACT_APP_INSTAGRAM_URL',
  tiktok: 'REACT_APP_TIKTOK_URL',
  facebook: 'REACT_APP_FACEBOOK_URL',
  pinterest: 'REACT_APP_PINTEREST_URL',
};

export function readSocialUrl(network: SocialNetwork, raw: string | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value || value === '#' || /^javascript:/i.test(value)) return null;

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:') return null;
  if (url.username || url.password) return null;
  if (!HOSTS[network].includes(url.hostname)) return null;
  if (!url.pathname || url.pathname === '/') return null;
  return url.href;
}

function urlFor(network: SocialNetwork): string | null {
  return readSocialUrl(network, process.env[ENV_NAMES[network]]);
}

export const socialLinks = {
  get instagram(): string | null {
    return urlFor('instagram');
  },
  get tiktok(): string | null {
    return urlFor('tiktok');
  },
  get facebook(): string | null {
    return urlFor('facebook');
  },
  get pinterest(): string | null {
    return urlFor('pinterest');
  },
};

export interface SocialLink {
  network: SocialNetwork;
  href: string;
  label: string;
}

const LABELS: Record<SocialNetwork, string> = {
  instagram: 'Follow LookBel on Instagram',
  tiktok: 'Follow LookBel on TikTok',
  facebook: 'Follow LookBel on Facebook',
  pinterest: 'Follow LookBel on Pinterest',
};

export function getConfiguredSocialLinks(): SocialLink[] {
  const networks: SocialNetwork[] = ['instagram', 'tiktok', 'facebook', 'pinterest'];
  return networks.flatMap((network) => {
    const href = socialLinks[network];
    return href ? [{ network, href, label: LABELS[network] }] : [];
  });
}
